import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export type ExportFormat = 'csv' | 'excel' | 'pdf' | 'pdf-light' | 'pdf-dark';

export interface ExportHeader {
  key: string;
  label: string;
  width?: number; // For PDF column width
}

export interface ExportOptions {
  title?: string;
  subtitle?: string;
  orientation?: 'portrait' | 'landscape';
}

/**
 * Escapes a value for CSV format
 */
function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  const stringValue = String(value);
  // If value contains comma, quote, or newline, wrap in quotes and escape existing quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

/**
 * Triggers a file download in the browser
 */
function downloadFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Detects if a header label represents a financial/currency field
 */
function isFinancialField(label: string): boolean {
  const financialKeywords = [
    'revenue', 'sales', 'total', 'amount', 'fee', 'cost', 'price',
    'tips', 'tax', 'gratuity', 'discount', 'refund', 'deferred',
    'royalty', 'marketing', 'gross', 'net', 'avg order', 'order value',
    'value', 'payment', 'balance', 'income', 'expense', 'profit'
  ];
  
  const lowerLabel = label.toLowerCase();
  return financialKeywords.some(keyword => lowerLabel.includes(keyword));
}

/**
 * Formats a value as currency by prepending $ if it's a numeric string
 */
function formatCurrencyValue(value: unknown): string | number {
  if (value === null || value === undefined) return '';
  
  // If it's a number, return as-is (will be formatted later)
  if (typeof value === 'number') return value;
  
  const stringValue = String(value);
  
  // Check if it's a numeric string (with optional decimals)
  // but not something like "N/A" or text
  const numericPattern = /^-?\d+\.?\d*$/;
  if (numericPattern.test(stringValue)) {
    return `$${stringValue}`;
  }
  
  return stringValue;
}

/**
 * Export data to CSV format
 */
export function exportToCSV<T extends Record<string, unknown>>(
  data: T[],
  headers: ExportHeader[],
  filename: string
): void {
  // Create header row
  const headerRow = headers.map(h => escapeCSVValue(h.label)).join(',');
  
  // Create data rows with currency formatting for financial fields
  const dataRows = data.map(row => 
    headers.map(h => {
      const value = row[h.key];
      // Apply currency formatting to financial fields
      if (isFinancialField(h.label)) {
        const formatted = formatCurrencyValue(value);
        return escapeCSVValue(formatted);
      }
      return escapeCSVValue(value);
    }).join(',')
  );
  
  // Combine header and data
  const csvContent = [headerRow, ...dataRows].join('\n');
  
  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const finalFilename = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  downloadFile(blob, finalFilename);
}

/**
 * Export data to Excel format
 */
export function exportToExcel<T extends Record<string, unknown>>(
  data: T[],
  headers: ExportHeader[],
  filename: string
): void {
  // Create worksheet data with headers, applying currency formatting to financial fields
  const worksheetData = [
    headers.map(h => h.label),
    ...data.map(row => headers.map(h => {
      const value = row[h.key];
      
      // Apply currency formatting to financial fields
      if (isFinancialField(h.label)) {
        if (typeof value === 'number') return value;
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (/^-?\d+\.?\d*$/.test(stringValue)) {
          return Number(stringValue);
        }
        return stringValue;
      }
      
      // Convert to appropriate type for Excel
      if (typeof value === 'number') return value;
      if (value === null || value === undefined) return '';
      return String(value);
    }))
  ];
  
  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  
  // Set column widths based on header configuration or content
  const colWidths = headers.map((h, i) => {
    if (h.width) return { wch: h.width };
    // Auto-calculate width based on header and max content length
    // Add extra width for currency symbol
    const extraWidth = isFinancialField(h.label) ? 2 : 0;
    const maxContentLength = Math.max(
      h.label.length,
      ...data.map(row => String(row[h.key] ?? '').length)
    );
    return { wch: Math.min(Math.max(maxContentLength + extraWidth, 10), 50) };
  });
  worksheet['!cols'] = colWidths;
  
  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  
  // Generate Excel file
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  
  const finalFilename = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  downloadFile(blob, finalFilename);
}

/**
 * Export data to PDF format
 */
export function exportToPDF<T extends Record<string, unknown>>(
  data: T[],
  headers: ExportHeader[],
  filename: string,
  options: ExportOptions = {}
): void {
  const { title, subtitle, orientation = 'portrait' } = options;
  
  // Create PDF document
  const doc = new jsPDF({
    orientation,
    unit: 'mm',
    format: 'a4'
  });
  
  let startY = 15;
  
  // Add title if provided
  if (title) {
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, startY);
    startY += 10;
  }
  
  // Add subtitle if provided
  if (subtitle) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(subtitle, 14, startY);
    doc.setTextColor(0);
    startY += 8;
  }
  
  // Prepare table data with currency formatting for financial fields
  const tableHeaders = headers.map(h => h.label);
  const tableData = data.map(row => 
    headers.map(h => {
      const value = row[h.key];
      if (value === null || value === undefined) return '';
      
      // Apply currency formatting to financial fields
      if (isFinancialField(h.label)) {
        const formatted = formatCurrencyValue(value);
        return String(formatted);
      }
      
      return String(value);
    })
  );
  
  // Brand orange color for header
  const brandOrange: [number, number, number] = [249, 115, 22];
  
  // Add table using autoTable
  autoTable(doc, {
    startY,
    head: [tableHeaders],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: brandOrange,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    bodyStyles: {
      fontSize: 8,
    },
    styles: {
      cellPadding: 2,
      overflow: 'linebreak',
    },
    columnStyles: headers.reduce((acc, h, i) => {
      if (h.width) {
        acc[i] = { cellWidth: h.width };
      }
      return acc;
    }, {} as Record<number, { cellWidth: number }>),
    margin: { left: 14, right: 14 },
  });
  
  // Add footer with date
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    const pageHeight = doc.internal.pageSize.height;
    doc.text(
      `Generated on ${new Date().toLocaleDateString()} | Page ${i} of ${pageCount}`,
      14,
      pageHeight - 10
    );
  }
  
  const finalFilename = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  doc.save(finalFilename);
}

/**
 * Main export function that handles all formats
 */
export function exportData<T extends Record<string, unknown>>(
  format: ExportFormat,
  data: T[],
  headers: ExportHeader[],
  filename: string,
  options?: ExportOptions
): void {
  switch (format) {
    case 'csv':
      exportToCSV(data, headers, filename);
      break;
    case 'excel':
      exportToExcel(data, headers, filename);
      break;
    case 'pdf':
      exportToPDF(data, headers, filename, options);
      break;
  }
}

